import React, { useContext, useEffect, useRef, useState, useCallback } from "react";
import {
  Routes,
  Route,
  Navigate,
  useParams,
  useLocation,
} from "react-router-dom";
import { AuthContext } from "../../../../context/AuthContext";
import ViewTests from "./ViewTests";
import Navbar from "../../../ui/Navbar";
import { fetchWithAuth } from "../../../../utils/api";

const serverUrl = process.env.REACT_APP_SERVER_URL;

export default function ViewClassHome() {
  const { isAuthenticated, user, primaryRole } = useContext(AuthContext);
  const [classInfo, setClassInfo] = useState({ id: null, name: "Loading..." });

  // Retrieve classId from URL parameters
  const { classId } = useParams();
  const location = useLocation();

  // Refs to manage modals in child components
  const testRef = useRef();

  const [pendingCounts, setPendingCounts] = useState(() => {
    try {
      const cached = sessionStorage.getItem(`pending_counts_${classId}`);
      if (cached) return JSON.parse(cached);
    } catch {}
    return { tests: 0, homeworks: 0 };
  });

  const handlePendingCountsChange = useCallback(
    (counts) => {
      if (counts && typeof counts === "object") {
        setPendingCounts((prev) => {
          const next = {
            tests: counts.tests !== undefined ? counts.tests : prev.tests,
            homeworks:
              counts.homeworks !== undefined ? counts.homeworks : prev.homeworks,
          };
          try {
            sessionStorage.setItem(
              `pending_counts_${classId}`,
              JSON.stringify(next)
            );
          } catch {}
          return next;
        });
      }
    },
    [classId]
  );

  // Simulate fetching class information based on class ID
  useEffect(() => {
    fetchWithAuth(
      `${serverUrl}/api/breadcrumbs/get?${new URLSearchParams({ classId })}`,
      { credentials: "include" }
    )
      .then((res) => {
        if (!res) return;
        return res.json();
      })
      .then((data) => {
        if (data?.class?.id) {
          setClassInfo({ name: data.class.name, id: data.class.id });
        } else {
          setClassInfo({ id: null, name: "Unknown Class" });
        }
      })
      .catch((err) => {
        setClassInfo({ id: null, name: "Unknown Class" });
      });
  }, [classId]);

  // Determine current active section from location pathname
  const pathParts = location.pathname.split("/").filter(Boolean);
  const lastSegment = pathParts[pathParts.length - 1];
  const currentPath =
    !lastSegment || lastSegment === classId ? "tests" : lastSegment.toLowerCase();
  const isHomework = currentPath === "homeworks" || currentPath === "homework";

  const isLanguageInst =
    user?.category === "LANGUAGE_LEARNING_INSTITUTE" ||
    user?.institute?.category === "LANGUAGE_LEARNING_INSTITUTE";

  const navLinks = [
    {
      text: isLanguageInst ? "Exams & Quizzes" : "Tests",
      to: `/student/view-class/${classId}`,
      id: `test-${classId}`,
      icon: isLanguageInst ? "fa-solid fa-file-signature" : "fa-solid fa-file",
      end: true,
      badgeCount: pendingCounts.tests,
    },
    {
      text: "Homeworks",
      to: `/student/view-class/${classId}/homeworks`,
      id: `homeworks-${classId}`,
      icon: "fa-solid fa-book-open",
      badgeCount: pendingCounts.homeworks,
    },
  ];

  const breadcrumbs = [
    { name: isLanguageInst ? "Batches" : "Home", path: "/student", id: "home" },
    {
      name: `${classInfo.name}`,
      path: `/student/view-class/${classInfo.id || classId}`,
      isLast: false,
      id: `class-${classInfo.id || classId}`,
    },
    {
      name: isHomework
        ? "Homeworks"
        : isLanguageInst
        ? "Exams & Quizzes"
        : "Tests",
      path: isHomework
        ? `/student/view-class/${classInfo.id || classId}/homeworks`
        : `/student/view-class/${classInfo.id || classId}`,
      isLast: true,
      id: isHomework ? "homeworks-crumb" : "tests-crumb",
    },
  ];

  // Redirect unauthenticated or unauthorized users
  if (!isAuthenticated || primaryRole !== "student") {
    return <Navigate to="/login" replace />;
  }

  return (
    <>
      <Navbar links={navLinks} breadcrumbs={breadcrumbs} />
      <div className="manage-class-content">
        <Routes>
          <Route
            index
            element={
              <ViewTests
                ref={testRef}
                classId={classId}
                defaultTab="tests"
                institute={user?.institute}
                onPendingCountsChange={handlePendingCountsChange}
                className={
                  classInfo.name !== "Loading..." &&
                  classInfo.name !== "Unknown Class"
                    ? classInfo.name
                    : ""
                }
              />
            }
          />
          <Route
            path="tests"
            element={
              <Navigate to={`/student/view-class/${classId}`} replace />
            }
          />
          <Route
            path="homeworks"
            element={
              <ViewTests
                ref={testRef}
                classId={classId}
                defaultTab="homeworks"
                institute={user?.institute}
                onPendingCountsChange={handlePendingCountsChange}
                className={
                  classInfo.name !== "Loading..." &&
                  classInfo.name !== "Unknown Class"
                    ? classInfo.name
                    : ""
                }
              />
            }
          />
          <Route
            path="homework"
            element={
              <Navigate to={`/student/view-class/${classId}/homeworks`} replace />
            }
          />
          <Route path="*" element={<Navigate to="." replace />} />
        </Routes>
      </div>
    </>
  );
}